import React, {useCallback, useMemo} from 'react';
import {Box} from '@mui/material';
import {useTranslation} from 'react-i18next';
import PartnerDataGrid from '../../../../../../containers/data-grid';
import {DataGridColumnAlignments, DataGridColumnPinnedTypes, DataGridColumnTypes, DataGridExoticColumnFields, useDataGridSavedState} from '../../../../../../../packages/data-grid';
import {CustomerMemberShipNames} from '../../../../../../../core/constants/enums';
import {CustomersMembersListDataGridCellKeys} from '../../../../../../../core/constants/table/data-grid-column-keys';
import {buildOnHoldMembersColumns, buildSharedMemberCells, dataGridSaveConfigs, renderAssignedCustomerCell, renderMemberActionsCell,} from './grid-shared';
import ResendActivationIconButton from './resend-activation-button';

/**
 * OnHold memberships PartnerDataGrid section (address-rich columns).
 */
const MembersOnHoldGridSection = ({
                                      members,
                                      searchData,
                                      countries,
                                      onAssign,
                                      onDetach,
                                      onDeactivate,
                                      onActivate,
                                      onDelete,
                                      canCreateMember,
                                      canOpenProfile,
                                      hasB2cWebsite,
                                      onResendActivation,
                                      onBatchResendActivation,
                                      getExportInfo,
                                      formLoading,
                                  }) => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.customers.members'});
    const dataGridSaveConfig = dataGridSaveConfigs[CustomerMemberShipNames.onHold];

    const columns = useMemo(() => {
        const baseColumns = buildOnHoldMembersColumns(t, true, {actionsWidth: hasB2cWebsite ? 110 : 70});
        if (!hasB2cWebsite) {
            return baseColumns;
        }
        return [
            {
                name: DataGridExoticColumnFields.selection,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                resizable: false,
                visibilityToggleable: false,
                pinnedToggleable: false,
                reorderable: false,
                sortable: false,
                pinned: true,
                pinnedType: DataGridColumnPinnedTypes.left,
                savable: false,
            },
            ...baseColumns,
        ];
    }, [hasB2cWebsite, t]);

    const footerActions = useMemo(() => {
        if (!hasB2cWebsite) {
            return [];
        }
        return [{
            name: 'resendActivation',
            title: t('resendActivation.batchAction'),
            visibilityConstraints: {minSelectedRows: 1},
        }];
    }, [hasB2cWebsite, t]);

    /**
     * Starts a batch resend for the grid selection, including select-all and exclusions.
     * @type {(function(any, any[], any[], boolean): Promise<void>)}
     */
    const onActionInvoked = useCallback(async (action, selectedRows, excludedRows, allRowsSelected) => {
        if (action?.name !== 'resendActivation') {
            return;
        }
        await onBatchResendActivation?.({selectedRows, excludedRows, allRowsSelected});
    }, [onBatchResendActivation]);

    const [dataGridColumns, , , dataGridDensity] = useDataGridSavedState({
        columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    const rows = useMemo(() => members?.map((member) => ({
        key: member.id,
        data: member,
        cells: {
            ...(hasB2cWebsite ? {[DataGridExoticColumnFields.selection]: null} : {}),
            ...buildSharedMemberCells(member, countries),
            [CustomersMembersListDataGridCellKeys.company]: renderAssignedCustomerCell(member, t),
            [CustomersMembersListDataGridCellKeys.actionButton]: (
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                    {
                        hasB2cWebsite && member?.memberShipName === CustomerMemberShipNames.onHold &&
                        <ResendActivationIconButton
                            label={t('rowActions.resendActivation')}
                            onClick={() => onResendActivation(member)}
                        />
                    }
                    {renderMemberActionsCell({
                        member,
                        onAssign,
                        onDetach,
                        onDeactivate,
                        onActivate,
                        onDelete,
                        onResendActivation,
                        canAssign: canCreateMember,
                        canOpenProfile,
                        hasB2cWebsite,
                    })}
                </Box>
            ),
        },
    })) ?? [], [
        canCreateMember,
        canOpenProfile,
        countries,
        hasB2cWebsite,
        onResendActivation,
        members,
        onActivate,
        onAssign,
        onDeactivate,
        onDelete,
        onDetach,
        t,
    ]);

    return (
        <PartnerDataGrid
            {...dataGridSaveConfig}
            initialLayoutState={{columns}}
            columns={dataGridColumns}
            rows={rows}
            hideFooterActions={!hasB2cWebsite}
            footerActions={footerActions}
            onActionInvoked={onActionInvoked}
            resetSelectionOnCurrentPageChange={false}
            state={{
                sortBy: searchData.orderBy,
                pagination: searchData.paginationInfo,
                loading: {state: searchData.loading || formLoading},
                density: dataGridDensity,
            }}
            getExportInfo={getExportInfo}
            onSortByChanged={(_, current) => searchData.setOrderBy(current)}
            onCurrentPageChanged={(_, current) => searchData.setPaginationInfo((prevState) => ({
                ...prevState,
                ...current,
            }))}
            onPageSizeChanged={(_, current) => searchData.setPaginationInfo((prevState) => ({
                ...prevState,
                ...current,
            }))}
        />
    );
};

export default MembersOnHoldGridSection;
